import Config from '@config';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import debounce from '@helpers/debounce';
import {
  getAddressLineSuggestions,
  validateFullAddress,
  getDeliverability,
  getDeliveryAvailableAddressLine,
} from '@api/interfaces/postalApi';
import { trackSearchSuggestions } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import FullAddressListItem from '@molecules/Autocomplete/ListItems/FullAddressListItem';
import logger from '@logger';
import useCustomer from '@hooks/useCustomer';
import KEYS from '@helpers/keys';
import type { AddressData, Suggestion } from '@occ/api-client';
import { OverriddenCustomerData } from '@api/interfaces/customerApi';
import Text from '@atoms/Text/Text';
import InputField from '@atoms/InputField/InputField';
import { StyledAutoComplete, StyledAutoCompleteContainer, StyledSpinner } from './FullAddressSearch.styles';
import { StyledEmptySlot, StyledEmptySlotHead } from '@molecules/PickupInStoreSearch/PickUpInStoreSearch.styles';
import useCustomRouter from '@hooks/useCustomRouter';
import paths from '@constants/paths';
import useCart from '@hooks/useCart';

export type FullAddressProps = {
  finishSubmission: (
    storeId: string,
    address: AddressParamsType,
    customerCoordinates: CustomerCoordinatesType,
    callback: () => void
  ) => Promise<any>;
  customerAddress: AddressData | null;
  suggestedAddress?: Suggestion;
};

const FullAddressSearch = ({ finishSubmission, customerAddress, suggestedAddress }: FullAddressProps) => {
  const MIN_SEARCH_LENGTH = 3;
  const [customValidity, setCustomValidity] = useState('');
  const [addressValue, setAddressValue] = useState('');
  const [useCustomerCoordinates, setUseCustomerCoordinates] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<Suggestion[]>([]);
  const [suggestionForYou, setSuggestionForYou] = useState(suggestedAddress);
  const [isLoading, setIsLoading] = useState(false);
  const [forceValidate, setForceValidate] = useState(false);
  const { cart } = useCart();
  const { customer } = useCustomer();
  const { t } = useTranslation('deliverypicker');
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  const router = useCustomRouter();
  const isCheckout = useMemo(() => router.pathname === paths.CHECKOUT, [router.pathname]);
  const getSuggestions = async (addressLine: string) => {
    setIsLoading(true);
    try {
      const suggestions = await getAddressLineSuggestions(addressLine.toLowerCase());
      setSearchSuggestions((suggestions.length <= 0 ? null : suggestions) as Suggestion[]);
      setIsLoading(false);
      trackSearchSuggestions(true, suggestions, isCheckout);
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: 'Could not get suggestions', message: e.message });
      }
      setIsLoading(false);
    }
  };

  const validateAddress = async (formattedAddress: string) => {
    try {
      const validateFullAddressResponse = await validateFullAddress(formattedAddress);
      if (validateFullAddressResponse.data.response.is_valid) {
        const { match } = validateFullAddressResponse.data.response;
        return Promise.resolve({ ...match, postalCode: match?.postalcode, streetNumber: match?.street_number });
      }
      return Promise.reject(new Error(t('deliveryPicker->error->invalidAddress')));
    } catch {
      return Promise.reject(new Error(t('deliveryPicker->error->invalidAddress')));
    }
  };

  const checkDeliveryAvailability = async (formattedAddress: string) => {
    let deliveryAvailableAddressLineResponse = null;
    try {
      deliveryAvailableAddressLineResponse = await getDeliveryAvailableAddressLine(formattedAddress);
    } catch {
      return Promise.reject(new Error(t('deliveryPicker->error->noDeliveryAvailable')));
    }

    if (!deliveryAvailableAddressLineResponse?.data?.isDeliveryAvailable) {
      return Promise.reject(new Error(t('deliveryPicker->error->noDeliveryAvailable')));
    }
    return Promise.resolve(deliveryAvailableAddressLineResponse);
  };

  const validateDeliverability = async (
    formattedAddress: string,
    customer: OverriddenCustomerData,
    postalCode?: string
  ) => {
    if (!postalCode) return Promise.reject(new Error(t('deliveryPicker->error->noDeliveryAvailable')));
    const deliverabilityResponse = await getDeliverability(encodeURIComponent(postalCode), customer.isB2BCustomer);

    if (deliverabilityResponse.data.deliverable) {
      if (deliverabilityResponse.data.isDivided) {
        try {
          await checkDeliveryAvailability(formattedAddress);
        } catch (e) {
          return Promise.reject(e);
        }
      }
      return Promise.resolve(deliverabilityResponse.data.deliveryStoreId);
    }
    return Promise.reject(new Error(t('deliveryPicker->error->noDeliveryAvailable')));
  };

  const validate = async (address: string) => {
    try {
      let addressParams: AddressParamsType;
      const addressLine2 = customerAddress?.line2 || '';
      if (useCustomerCoordinates && customerAddress) {
        addressParams = {
          addressLine: customerAddress.line1,
          addressLine2,
          postalCode: customerAddress.postalCode || '',
          locality: customerAddress.town,
          companyName: customer?.companyAlternativeName || customerAddress?.companyName,
        };
      } else {
        const validatedParams = await validateAddress(address);
        addressParams = {
          addressLine: `${validatedParams.street || ''} ${validatedParams.streetNumber || ''}${
            validatedParams.letter || ''
          }`,
          postalCode: validatedParams.postalCode,
          locality: validatedParams.locality,
          addressLine2,
        };
      }
      // customer is probably not undefined at this point
      const deliveryStoreId = await validateDeliverability(address, customer!, addressParams.postalCode);
      return Promise.resolve({ addressParams, deliveryStoreId });
    } catch (error) {
      if (error instanceof Error) {
        setCustomValidity(error.message);
      }
      return Promise.reject(new Error('Validation failed'));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetAddressSuggestions = useCallback(
    debounce(getSuggestions, Config.TIMEOUT.addressSearchGetSuggestionsMs),
    []
  );

  const onClear = () => {
    setCustomValidity('');
    setUseCustomerCoordinates(false);

    setAddressValue('');
    setSearchSuggestions([]);
    debouncedGetAddressSuggestions.cancel();
  };

  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setCustomValidity('');
    setSuggestionForYou(undefined);
    setUseCustomerCoordinates(false);
    const value = e?.currentTarget ? e.currentTarget.value : '';

    setAddressValue(value);
    if (value.length >= MIN_SEARCH_LENGTH) {
      debouncedGetAddressSuggestions(value);
    } else {
      setSearchSuggestions([]);
      debouncedGetAddressSuggestions.cancel();
    }
  };

  const addressListItemOnClick = async (suggestion: Suggestion) => {
    const { street, street_number, letter, postalcode, locality } = suggestion;
    const value = `${street}${street_number || letter ? ' ' : ''}${street_number}${letter}, ${postalcode} ${locality}`;

    setForceValidate(true);
    setCustomValidity('');
    setAddressValue(value);
    await submitAddress(value);
  };

  const submitAddress = async (addressValue: string) => {
    if (!addressValue) return;

    setIsLoading(true);
    try {
      const { addressParams = {} as AddressParamsType, deliveryStoreId = '' } = await validate(addressValue);
      await finishSubmission(
        deliveryStoreId,
        addressParams,
        {
          longitude: '',
          latitude: '',
          firstName: cart?.deliveryAddress?.firstName || '',
          lastName: cart?.deliveryAddress?.lastName || '',
        },
        () => setIsLoading(false)
      );
    } catch {
      setIsLoading(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYS.TAB:
        e.currentTarget.blur();
        break;
      case KEYS.UP:
        e.preventDefault();
        break;
      case KEYS.DOWN:
        e.preventDefault();
        autoCompleteRef.current?.querySelector<HTMLOptionElement>('[role="option"]')?.focus();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!customerAddress) return;
    if (customerAddress.longitude && customerAddress.latitude) {
      setUseCustomerCoordinates(true);
    }
  }, [customerAddress]);

  const emptyAutocompleteFragment =
    !searchSuggestions && addressValue.length >= MIN_SEARCH_LENGTH ? (
      <StyledEmptySlot>
        <StyledEmptySlotHead>
          {t('deliveryPicker->autoComplete->pickupLocation->emptySlot->head', {
            search: addressValue,
          })}
        </StyledEmptySlotHead>
      </StyledEmptySlot>
    ) : (
      ''
    );

  const suggestionForYouFragment = (
    <>
      <Text type="subtitle">{t('deliveryPicker->autoComplete->suggestionForYou')}</Text>
      <StyledAutoComplete
        errorMessage={''}
        searchTerm=""
        suggestions={[suggestionForYou]}
        ListComponent={FullAddressListItem}
        listComponentOnClick={addressListItemOnClick}
        emptySlot={''}
        showAsCards
        getKeyFunction={(suggestion) =>
          `${suggestion.street}-${suggestion.street_number}-${suggestion.postalcode}${suggestion.letter}-${suggestion.locality}`
        }
      />
    </>
  );

  const autocompleteFragment = (
    <StyledAutoComplete
      errorMessage={''}
      searchTerm={addressValue}
      suggestions={searchSuggestions}
      ListComponent={FullAddressListItem}
      listComponentOnClick={addressListItemOnClick}
      emptySlot={!isLoading && emptyAutocompleteFragment}
      showAsCards
      getKeyFunction={(suggestion) =>
        `${suggestion.street}-${suggestion.street_number}-${suggestion.postalcode}${suggestion.letter}-${suggestion.locality}`
      }
      ref={autoCompleteRef}
    />
  );

  return (
    <>
      <Text type="body" size="medium" color="black">
        {t('deliveryPicker->info->homeDeliveryAddress')}
      </Text>

      <InputField
        type="search"
        aria-label="searchHomeAddress"
        name="searchHomeAddress"
        placeholder={t('deliveryPicker->placeholder->address')}
        value={addressValue}
        onChange={onChange}
        onClear={onClear}
        onKeyDown={onKeyDown}
        customValidity={addressValue && customValidity}
        autoComplete="off"
        clearable
        showPrependIcon
        autoFocus
        roundedCorners
        forceValidate={forceValidate}
      />
      <StyledAutoCompleteContainer>
        {!isLoading && suggestionForYou?.street && suggestionForYouFragment}
        {!isLoading && autocompleteFragment}
        {isLoading && <StyledSpinner size={32} color="black" />}
      </StyledAutoCompleteContainer>
    </>
  );
};

export default FullAddressSearch;
